/**
 * Article-page enhancement helpers for the BigLoads blog redesign.
 *
 * All functions here are PURE and SSR-safe (no window/document access) so the
 * article page renders identically server-side (important for SEO/AEO — the
 * auto quick-answer, TOC, and FAQ must be present in first-pass HTML for
 * Google featured snippets and LLM answer-engine citation).
 *
 * The article body comes from Strapi as an HTML string (`htmlDescription`),
 * sanitized by util/htmlSanitizer. These helpers parse that same HTML string
 * with lightweight regex (no DOM) to derive:
 *   - a Table of Contents from the H2/H3 headings
 *   - a "Quick Answer" from the first substantive paragraph
 *   - an FAQ list from any "?"-ending H2/H3 followed by a paragraph
 *
 * Heading IDs are generated with the SAME slug function used by the sanitizer
 * rehype plugin, so TOC jump-links line up with the injected heading ids.
 */

// Decode the HTML entities that rehype-parse resolves so that our regex-based
// plain-text matches the sanitizer's DOM-text basis. This is what keeps TOC
// anchors in sync with the heading ids injected by the sanitizer (both slug
// from the SAME decoded text). Covers named + numeric (decimal & hex) entities.
const decodeEntities = str => {
  if (!str) return '';
  const named = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    nbsp: ' ',
    ndash: '-',
    mdash: '-',
    rsquo: "'",
    lsquo: "'",
    ldquo: '"',
    rdquo: '"',
    hellip: '...',
  };
  return String(str)
    .replace(/&#x([0-9a-f]+);/gi, (_, h) => {
      try {
        return String.fromCodePoint(parseInt(h, 16));
      } catch (e) {
        return ' ';
      }
    })
    .replace(/&#(\d+);/g, (_, d) => {
      try {
        return String.fromCodePoint(parseInt(d, 10));
      } catch (e) {
        return ' ';
      }
    })
    .replace(/&([a-z]+);/gi, (m, name) =>
      Object.prototype.hasOwnProperty.call(named, name.toLowerCase())
        ? named[name.toLowerCase()]
        : ' '
    );
};

// Strip tags + decode entities + collapse whitespace to get the SAME plain
// text the sanitizer's rehype text nodes yield.
const stripTags = html =>
  decodeEntities((html == null ? '' : String(html)).replace(/<[^>]*>/g, ''))
    .replace(/\s+/g, ' ')
    .trim();

// Slugify heading text into a stable, URL-safe id. MUST match the slug logic
// used in util/htmlSanitizer's addHeadingIds plugin (which slugs from decoded
// DOM text). We therefore strip tags + decode entities FIRST, then apply the
// identical [^a-z0-9\s-] transform.
export const slugifyHeading = text => {
  const s = stripTags(text)
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
  return s || null;
};

/**
 * Extract a Table of Contents from H2 (and nested H3) headings in the body.
 * Returns [{ id, text, level }]. De-dupes ids the same way the sanitizer does
 * (suffixing -2, -3, ...) so TOC anchors match rendered heading ids exactly.
 */
export const extractToc = html => {
  if (!html || typeof html !== 'string') return [];
  const headingRe = /<(h2|h3)[^>]*>([\s\S]*?)<\/\1>/gi;
  const seen = {};
  const toc = [];
  let m;
  while ((m = headingRe.exec(html)) !== null) {
    const level = m[1].toLowerCase() === 'h2' ? 2 : 3;
    const text = stripTags(m[2]);
    if (!text) continue;
    let base = slugifyHeading(text);
    if (!base) continue;
    // Match sanitizer de-dupe behavior.
    if (seen[base] == null) {
      seen[base] = 1;
    } else {
      seen[base] += 1;
      base = `${base}-${seen[base]}`;
    }
    toc.push({ id: base, text, level });
  }
  return toc;
};

/**
 * Derive a "Quick Answer" snippet from the first substantive paragraph of the
 * body (featured-snippet / AEO bait). We take the first <p> whose plain-text
 * length is at least `minLen`, trimmed to a readable length. Returns a string
 * or null.
 */
export const extractQuickAnswer = (html, opts = {}) => {
  const minLen = opts.minLen || 80;
  const maxLen = opts.maxLen || 320;
  if (!html || typeof html !== 'string') return null;
  const paraRe = /<p[^>]*>([\s\S]*?)<\/p>/gi;
  let m;
  while ((m = paraRe.exec(html)) !== null) {
    const text = stripTags(m[1]);
    if (text.length >= minLen) {
      if (text.length <= maxLen) return text;
      // Trim at a sentence/word boundary near maxLen.
      const cut = text.slice(0, maxLen);
      const lastStop = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf('! '), cut.lastIndexOf('? '));
      if (lastStop > minLen) return cut.slice(0, lastStop + 1).trim();
      const lastSpace = cut.lastIndexOf(' ');
      return `${cut.slice(0, lastSpace > 0 ? lastSpace : maxLen).trim()}…`;
    }
  }
  return null;
};

/**
 * Build an FAQ list from the body: any H2/H3 whose text ends with "?" and is
 * immediately followed by paragraph/list content becomes a Q/A pair. Used both
 * for the on-page FAQ accordion and (by the caller) FAQPage JSON-LD.
 * Returns [{ question, answer }].
 */
export const extractFaq = (html, opts = {}) => {
  const max = opts.max || 12;
  if (!html || typeof html !== 'string') return [];
  const faq = [];
  // Match a heading that is a question, then capture following content up to
  // the next heading.
  const re = /<(h2|h3)[^>]*>([\s\S]*?)<\/\1>([\s\S]*?)(?=<h2|<h3|$)/gi;
  let m;
  while ((m = re.exec(html)) !== null && faq.length < max) {
    const question = stripTags(m[2]);
    if (!question.endsWith('?')) continue;
    const answer = stripTags(m[3]);
    if (answer.length < 20) continue;
    faq.push({ question, answer: answer.length > 600 ? `${answer.slice(0, 600).trim()}…` : answer });
  }
  return faq;
};

/**
 * Rough read-time estimate from the body plain text (200 wpm), min 1 minute.
 */
export const estimateReadTime = html => {
  const words = stripTags(html).split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
};

export const plainTextLength = html => stripTags(html).length;

/* =====================================================================
   BLOCKS-BASED EXTRACTORS
   72 of 73 BigLoads articles store their body in the Strapi rich-text
   `description` field (an array of block nodes), NOT `htmlDescription`.
   These walk that blocks array so TOC / quick-answer / FAQ / heading-ids
   work on the description path too. slugBlockText MUST match slugifyHeading.
   ===================================================================== */

// Concatenate the plain text of a block node's children (recursively).
export const blockText = node => {
  if (!node) return '';
  if (typeof node === 'string') return node;
  if (node.type === 'text') return node.text || '';
  if (Array.isArray(node.children)) return node.children.map(blockText).join('');
  return '';
};

// Slug a block heading identically to slugifyHeading (which strips/decodes
// then applies [^a-z0-9\s-]). Block text has no tags/entities already, but we
// route through slugifyHeading so the transform stays in ONE place.
export const slugifyBlockHeading = node => slugifyHeading(blockText(node));

// Build TOC from description blocks (h2/h3). De-dupe (-2/-3) to match the
// id-injection in StrapiContentRenderer.
export const extractTocFromBlocks = blocks => {
  if (!Array.isArray(blocks)) return [];
  const seen = Object.create(null);
  const toc = [];
  for (const b of blocks) {
    if (!b || b.type !== 'heading') continue;
    const level = b.level === 3 ? 3 : b.level === 2 ? 2 : null;
    if (!level) continue; // only h2/h3 in the TOC
    const text = blockText(b).replace(/\s+/g, ' ').trim();
    if (!text) continue;
    let base = slugifyHeading(text);
    if (!base) continue;
    if (seen[base] == null) seen[base] = 1;
    else {
      seen[base] += 1;
      base = `${base}-${seen[base]}`;
    }
    toc.push({ id: base, text, level });
  }
  return toc;
};

export const extractQuickAnswerFromBlocks = (blocks, opts = {}) => {
  const minLen = opts.minLen || 80;
  const maxLen = opts.maxLen || 320;
  if (!Array.isArray(blocks)) return null;
  for (const b of blocks) {
    if (!b || b.type !== 'paragraph') continue;
    const text = blockText(b).replace(/\s+/g, ' ').trim();
    if (text.length >= minLen) {
      if (text.length <= maxLen) return text;
      const cut = text.slice(0, maxLen);
      const lastStop = Math.max(
        cut.lastIndexOf('. '),
        cut.lastIndexOf('! '),
        cut.lastIndexOf('? ')
      );
      if (lastStop > minLen) return cut.slice(0, lastStop + 1).trim();
      const lastSpace = cut.lastIndexOf(' ');
      return `${cut.slice(0, lastSpace > 0 ? lastSpace : maxLen).trim()}\u2026`;
    }
  }
  return null;
};

export const extractFaqFromBlocks = (blocks, opts = {}) => {
  const max = opts.max || 12;
  if (!Array.isArray(blocks)) return [];
  const faq = [];
  for (let i = 0; i < blocks.length && faq.length < max; i++) {
    const b = blocks[i];
    if (!b || b.type !== 'heading') continue;
    const question = blockText(b).replace(/\s+/g, ' ').trim();
    if (!question.endsWith('?')) continue;
    // Gather following paragraph/list blocks until the next heading.
    let answer = '';
    for (let j = i + 1; j < blocks.length; j++) {
      if (blocks[j] && blocks[j].type === 'heading') break;
      const t = blockText(blocks[j]).replace(/\s+/g, ' ').trim();
      if (t) answer += (answer ? ' ' : '') + t;
      if (answer.length > 600) break;
    }
    if (answer.length < 20) continue;
    faq.push({
      question,
      answer: answer.length > 600 ? `${answer.slice(0, 600).trim()}\u2026` : answer,
    });
  }
  return faq;
};

export const estimateReadTimeFromBlocks = blocks => {
  if (!Array.isArray(blocks)) return null;
  const words = blocks
    .map(blockText)
    .join(' ')
    .split(/\s+/)
    .filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
};
